/*
 * fullstar-motion.js — the prototype's scroll-scrub motion engine.
 *
 * ⚠️ THE BODY OF THIS FILE IS A VERBATIM COPY, NOT A REIMPLEMENTATION.
 *
 * Everything between the two "PROTOTYPE CODE" markers was extracted
 * mechanically from
 *
 *     Fullstar store website build/Fullstar Store.dc.html
 *
 * (the helper trio at lines 1596–1598, and the motion functions at lines
 * 2975–3367) by scripts/content-plan/17-design-assets.mjs's sibling extraction
 * step. It was not retyped and it was not adapted. If the motion here differs
 * from the prototype, this file is stale — re-extract it rather than patching
 * it by hand, or the two drift apart in ways nobody can see.
 *
 * HOW IT WORKS, IN ONE PARAGRAPH
 * Each full-screen scene carries data-scene. On every scroll the engine works
 * out how far through that scene you are, as a number from 0 to 1, and paints
 * every animated element from that number. Nothing is time-based, so scrolling
 * backwards runs the animation backwards, and stopping halfway leaves it
 * halfway. Elements carrying data-a get a generic fade / rise / scale from the
 * same number; the named scenes (open, problem, proof, cinema) get their own
 * handler on top.
 *
 * WHAT IS ADDED HERE, AND ONLY THIS
 *   1. The scroll, resize and rAF wiring, which in the prototype lived in the
 *      React component's lifecycle rather than in these functions.
 *   2. prefers-reduced-motion: the engine is not started, and every animated
 *      element is painted once at its finished state, so the page reads as a
 *      normal static layout. The prototype had no such handling — it had no
 *      accessibility obligation, and this theme does.
 *   3. A guard so it does nothing on pages with no scenes on them.
 */
(function () {
  'use strict';

  /* ────────────────────────── BEGIN PROTOTYPE CODE ────────────────────────── */

  const cl = (v, a, b) => Math.max(a, Math.min(b, v));
  const eo = (t) => 1 - Math.pow(1 - t, 3);
  const sg = (v, a, b) => cl((v - a) / (b - a), 0, 1);
  
  function fsBindCompare() {
    document.querySelectorAll("[data-fs-compare]").forEach(function (box) {
      if (box.__fsBound) return;
      box.__fsBound = 1;
      box.addEventListener("scroll", fsPaintCompare, { passive: true });
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => fsPaintCompare());
        ro.observe(box);
        if (box.firstElementChild) ro.observe(box.firstElementChild);
      }
    });
    if (!window.__fsCompareResize) {
      window.__fsCompareResize = 1;
      window.addEventListener("resize", fsPaintCompare);
    }
    fsPaintCompare();
  }
  
  function fsPaintCompare() {
    document.querySelectorAll("[data-fs-compare]").forEach(function (box) {
      const sec = box.closest("section") || document;
      const over = box.scrollWidth - box.clientWidth > 8;
      const cue = sec.querySelector("[data-fs-compare-cue]");
      if (cue) cue.style.display = over ? "inline" : "none";
      const fade = sec.querySelector("[data-fs-compare-fade]");
      if (fade) fade.style.opacity = over && box.scrollLeft < box.scrollWidth - box.clientWidth - 8 ? "1" : "0";
    });
  }
  
  function fsPaintStage() {
    try { fslPaint(); } catch (e) {}
    try {
      const stage = document.querySelector("[data-fs-stage]");
      if (!stage) return;
      const k = window.__fsK == null ? 1 : Number(window.__fsK);
      const r = stage.getBoundingClientRect();
      const total = Math.max(1, stage.offsetHeight - window.innerHeight);
      const p = cl(-r.top / total, 0, 1);
      if (window.__fsLastP != null && Math.abs(window.__fsLastP - p) < 0.0008 && window.__fsLastK === k) return;
      window.__fsLastP = p; window.__fsLastK = k;
  
      const bg = stage.querySelector("[data-fs-bg]");
      if (bg) bg.style.transform = "translate3d(0," + (-100 * p * k).toFixed(1) + "px,0) scale(1.06)";
      const frame = stage.querySelector("[data-fs-frame]");
      if (frame) frame.style.transform = "translate3d(0," + ((0.5 - p) * 72 * k).toFixed(1) + "px,0)";
  
      const inA = sg(p, .26, .44), inB = sg(p, .56, .74);
      const o = [1 - inA, inA - inB, inB];
      const lay = [1, inA, inB];
      stage.querySelectorAll("[data-fs-layer]").forEach((el) => {
        const i = Number(el.getAttribute("data-fs-layer"));
        const v = cl(lay[i], 0, 1);
        el.style.opacity = String(v);
        el.style.transform = "scale(" + (1 + 0.03 * (1 - v)).toFixed(3) + ")";
      });
      const pill = stage.querySelector("[data-fs-pill]");
      if (pill) pill.style.opacity = (cl(inA, 0, 1) * (1 - cl(sg(p, .78, .92), 0, 1) * .85)).toFixed(3);
      stage.querySelectorAll("[data-fs-text]").forEach((el) => {
        const i = Number(el.getAttribute("data-fs-text"));
        const v = cl(o[i], 0, 1);
        el.style.opacity = String(v);
        el.style.transform = "translate3d(0," + ((1 - v) * 18).toFixed(1) + "px,0)";
        el.style.pointerEvents = v > .55 ? "auto" : "none";
      });
      stage.querySelectorAll("[data-fs-dot]").forEach((el) => {
        const i = Number(el.getAttribute("data-fs-dot"));
        const on = cl(o[i], 0, 1) > .5;
        el.style.background = on ? "var(--red-primary)" : "var(--gray-300)";
        el.style.transform = "scale(" + (on ? 1 : .72) + ")";
        el.style.transition = "background-color 150ms linear";
      });
      const t = sg(p, .68, .98);
      stage.querySelectorAll("[data-fs-tile]").forEach((el) => {
        const c = Number(el.getAttribute("data-c"));
        const rr = Number(el.getAttribute("data-r"));
        if (!el.style.backgroundPosition) el.style.backgroundPosition = el.getAttribute("data-pos") || "0% 0%";
        el.style.transform = "translate3d(" + ((c - 1.5) * 13 * t * k).toFixed(1) + "px," + ((rr - 1) * 15 * t * k).toFixed(1) + "px,0) rotate(" + ((((c + rr) % 3) - 1) * 2.4 * t).toFixed(2) + "deg) scale(" + (1 - .06 * t).toFixed(3) + ")";
      });
      const prog = stage.querySelector("[data-fs-prog]");
      if (prog) prog.style.width = (p * 100).toFixed(1) + "%";
    } catch (e) { window.__fsPaintErr = String(e && e.stack || e); }
  }
  
  function fslVideos() {
    const vh = window.innerHeight || 0;
    document.querySelectorAll("video[data-fsl-vid]").forEach((v) => {
      if (!v.__init) {
        v.__init = 1;
        v.muted = true; v.loop = true; v.playsInline = true;
        v.setAttribute("playsinline", "");
      }
      const r = v.getBoundingClientRect();
      const onScreen = r.bottom > -80 && r.top < vh + 80 && r.width > 4 && v.offsetParent !== null;
      if (onScreen) {
        if (v.paused) { const pr = v.play(); if (pr && pr.catch) pr.catch(() => {}); }
      } else if (!v.paused) {
        v.pause();
      }
    });
  }
  
  function fslParse(el) {
    if (el.__a) return el.__a;
    const raw = el.getAttribute("data-a") || "";
    const parts = raw.split(";").map((s) => s.trim()).filter(Boolean).map((s) => {
      const t = s.split(/\s+/);
      return { kind: t[0], a: parseFloat(t[1]), b: parseFloat(t[2]), v0: t[3] === undefined ? null : parseFloat(t[3]), v1: t[4] === undefined ? null : parseFloat(t[4]) };
    });
    el.__a = parts;
    return parts;
  }
  
  function fslGeneric(scope, p) {
    scope.querySelectorAll("[data-a]").forEach((el) => {
      let op = null, tr = "";
      fslParse(el).forEach((d) => {
        const t = eo(sg(p, d.a, d.b));
        if (d.kind === "fade") {
          const v0 = d.v0 == null ? 0 : d.v0, v1 = d.v1 == null ? 1 : d.v1;
          const v = v0 + (v1 - v0) * t;
          op = op == null ? v : Math.min(op, v);
        } else if (d.kind === "up") {
          op = op == null ? t : Math.min(op, t);
          tr += " translate3d(0," + ((1 - t) * 34).toFixed(1) + "px,0)";
        } else if (d.kind === "scale") {
          const s0 = d.v0 == null ? 1.1 : d.v0, s1 = d.v1 == null ? 1 : d.v1;
          tr += " scale(" + (s0 + (s1 - s0) * t).toFixed(4) + ")";
        }
      });
      if (el.__base === undefined) el.__base = el.style.transform || "";
      if (op != null) el.style.opacity = op.toFixed(3);
      if (tr) el.style.transform = (el.__base + " " + tr).trim();
    });
  }
  
  function fslPaint() {
    try {
      const k = window.__fslK == null ? 1 : Number(window.__fslK);
      const doc = document.documentElement;
      const scrub = document.querySelector("[data-fsl-scrub]");
      if (scrub) scrub.style.width = (cl(window.pageYOffset / Math.max(1, doc.scrollHeight - window.innerHeight), 0, 1) * 100).toFixed(2) + "%";
      let activeName = null;
  
      document.querySelectorAll("[data-scene]").forEach((sec) => {
        const r = sec.getBoundingClientRect();
        const total = Math.max(1, sec.offsetHeight - window.innerHeight);
        const band = sec.hasAttribute("data-band");
        const p = window.__fslForce != null ? Number(window.__fslForce)
          : band ? cl((window.innerHeight * .92 - r.top) / (window.innerHeight * .62), 0, 1)
          : cl(-r.top / total, 0, 1);
        const visible = r.top < window.innerHeight * .5 && r.bottom > window.innerHeight * .5;
        if (visible) activeName = sec.getAttribute("data-scene");
        if (r.bottom < -200 || r.top > window.innerHeight + 200) return;
        fslGeneric(sec, p);
        const name = sec.getAttribute("data-scene");
        if (band) {
          sec.querySelectorAll("[data-fsl-step]").forEach((el, i) => {
            const t = eo(sg(p, .04 + i * .06, .5 + i * .06));
            el.style.opacity = String(t);
            el.style.transform = "translate3d(0," + ((1 - t) * 44).toFixed(1) + "px,0)";
          });
          return;
        }
        if (name === "open") fslOpen(sec, p, k);
        else if (name === "problem") fslProblem(sec, p);
        else if (name === "cinema") fslCinema(sec, p, k);
        else if (name === "proof") fslProof(sec, p);
      });
  
      const head = document.querySelector("[data-fsl-head]");
      if (head) {
        const onLight = ["families", "wordmark"].indexOf(activeName) >= 0;
        head.style.color = onLight ? "var(--dark-gray)" : "#fff";
        const nav = head.querySelector("[data-fsl-nav]");
        if (nav) nav.style.display = window.innerWidth < 820 ? "none" : "flex";
        const logo = head.querySelector("img");
        if (logo) logo.style.filter = onLight ? "invert(1) brightness(.35) saturate(0)" : "none";
      }
    } catch (e) { window.__fslErr = String(e && e.stack || e); }
  }
  
  function fslOpen(sec, p, k) {
    if (!window.__fslT0) window.__fslT0 = Date.now();
    const vid = sec.querySelector("[data-fsl-hero-video]");
    if (vid) {
      const enter = eo(cl((Date.now() - window.__fslT0) / 900, 0, 1));
      vid.style.opacity = String(enter);
      vid.style.transform = "scale(" + (1.06 - enter * .06 + p * .07 * k).toFixed(4) + ")";
    }
    const scrim = sec.querySelector("[data-fsl-hero-scrim]");
    if (scrim) scrim.style.opacity = String(.55 + sg(p, .3, 1) * .45);
    const hint = sec.querySelector("[data-fsl-hint]");
    if (hint) {
      hint.style.display = window.innerHeight < 640 ? "none" : "block";
      hint.style.opacity = String(1 - sg(p, .06, .3));
    }
  }
  
  function fslProblem(sec, p) {
    const timer = sec.querySelector("[data-fsl-timer]");
    if (timer) {
      const t = sg(p, .3, .74);
      const secs = Math.round(t * 520);
      timer.textContent = Math.floor(secs / 60) + ":" + String(secs % 60).padStart(2, "0");
    }
    const bubble = sec.querySelector("[data-fsl-bubble]");
    if (bubble) {
      const t = eo(sg(p, .72, .9));
      bubble.style.opacity = String(t);
      bubble.style.transform = "translate3d(0," + ((1 - t) * 22).toFixed(1) + "px,0) scale(" + (.94 + t * .06).toFixed(3) + ")";
    }
  }
  
  function fslCinema(sec, p, k) {
    const rows = [].slice.call(sec.querySelectorAll("[data-fsl-cin-row]"));
    const n = Math.max(1, rows.length);
    const t = cl(p, 0, .9999) * n;
    let idx = Math.floor(t);
    if (window.__fslPick && Date.now() < window.__fslPick.until) idx = cl(window.__fslPick.i, 0, n - 1);
    const narrow = window.innerWidth < 860;
    const short = window.innerHeight < 800;
    const tight = window.innerHeight < 760;
    const eyebrow = sec.querySelector("[data-fsl-cinema-eyebrow]");
    if (eyebrow) eyebrow.style.display = tight ? "none" : "block";
    sec.querySelectorAll("[data-fsl-tab]").forEach((el) => { el.style.padding = tight ? "8px 16px" : "11px 22px"; });
    const models = sec.querySelector("[data-fsl-models]");
    if (models) {
      models.style.flexWrap = "nowrap";
      models.style.overflowX = "auto";
      models.style.paddingBottom = "2px";
    }
    sec.querySelectorAll("[data-fsl-model]").forEach((el) => {
      el.style.flex = "0 0 auto";
      el.style.padding = tight ? "6px 12px" : "9px 16px";
      el.style.fontSize = tight ? "11px" : "12px";
    });
    const gridEl = sec.querySelector("[data-fsl-cinema-grid]");
    const range = (gridEl && gridEl.getAttribute("data-range")) === "mandolines" ? "mandolines" : "choppers";
    const media = sec.querySelector("[data-fsl-cinema-media]");
    if (media) {
      media.style.maxHeight = narrow ? "min(26vh,220px)" : short ? "min(46vh,380px)" : "min(62vh,540px)";
      media.style.order = narrow ? "-1" : "0";
    }
    sec.querySelectorAll("[data-fsl-tab]").forEach((el) => {
      const on = el.getAttribute("data-fsl-tab") === range;
      el.style.background = on ? "#fff" : "transparent";
      el.style.color = on ? "var(--dark-gray)" : "#fff";
      el.style.borderColor = on ? "#fff" : "rgba(255,255,255,.4)";
    });
    sec.querySelectorAll("[data-fsl-model]").forEach((el, i) => {
      const on = i === idx;
      el.style.background = on ? "#fff" : "transparent";
      el.style.color = on ? "var(--dark-gray)" : "rgba(255,255,255,.8)";
      el.style.borderColor = on ? "#fff" : "rgba(255,255,255,.28)";
    });
    const ease = "320ms var(--ease-standard)";
    sec.querySelectorAll("[data-fsl-cin-shot]").forEach((el, i) => {
      const on = i === idx;
      el.style.transition = "opacity " + ease + ", transform " + ease;
      el.style.opacity = String(on ? 1 : 0);
      el.style.transform = "scale(" + (on ? 1 : 1.03 + .02 * k) + ")";
    });
    sec.querySelectorAll("[data-fsl-cin-copy]").forEach((el, i) => {
      const on = i === idx;
      el.style.transition = "opacity 280ms var(--ease-standard), transform 280ms var(--ease-standard)";
      el.style.opacity = String(on ? 1 : 0);
      el.style.transform = "translate3d(0," + (on ? 0 : 18) + "px,0)";
      el.style.pointerEvents = on ? "auto" : "none";
    });
    rows.forEach((el, i) => {
      const on = i === idx;
      el.style.transition = "opacity 220ms var(--ease-standard), border-color 220ms var(--ease-standard)";
      el.style.opacity = String(on ? 1 : .34);
      el.style.borderBottomColor = on ? "rgba(255,88,93,.8)" : "rgba(255,255,255,.1)";
    });
  
    const rail = sec.querySelector("[data-fsl-cinema-rail]");
    const stack = sec.querySelector("[data-fsl-cinema-stack]");
    const grid = sec.querySelector("[data-fsl-cinema-grid]");
    const copies = [].slice.call(sec.querySelectorAll("[data-fsl-cin-copy]"));
    if (rail && stack && grid && copies.length) {
      const c = fslCinemaMetrics(sec, copies, stack, rail, grid);
      const cs = getComputedStyle(grid);
      const avail = grid.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      let mode = 0;
      if (avail < c.headH + c.full) mode = 1;
      if (avail < c.headH + c.noChips) mode = 2;
      const stackH = mode === 0 ? c.full : mode === 1 ? c.noChips : c.noPara;
      stack.style.minHeight = Math.ceil(stackH) + "px";
      copies.forEach((el) => {
        const para = el.querySelector("p");
        const chips = el.querySelector("[data-fsl-cin-specs]");
        if (chips) chips.style.display = mode >= 1 ? "none" : "flex";
        if (para) para.style.display = mode >= 2 ? "none" : "block";
      });
      rail.style.display = !narrow && avail >= c.headH + stackH + c.railH + 24 ? "grid" : "none";
    }
  }
  
  function fslCinemaMetrics(sec, copies, stack, rail, grid) {
    const key = Math.round(window.innerWidth) + "|" + (window.innerHeight < 760 ? "t" : "n") + "|" + (grid.getAttribute("data-range") || "") + "|" + copies.length;
    if (sec.__cin && sec.__cinKey === key) return sec.__cin;
    const prevStack = stack.style.minHeight, prevRail = rail.style.display;
    stack.style.minHeight = "0px";
    rail.style.display = "grid";
    copies.forEach((el) => {
      const para = el.querySelector("p");
      const chips = el.querySelector("[data-fsl-cin-specs]");
      if (chips) chips.style.display = "flex";
      if (para) para.style.display = "block";
    });
    let full = 0, noChips = 0, noPara = 0;
    copies.forEach((el) => {
      const h = el.offsetHeight;
      const para = el.querySelector("p");
      const chips = el.querySelector("[data-fsl-cin-specs]");
      const chipsH = chips ? chips.offsetHeight + parseFloat(getComputedStyle(chips).marginTop || 0) : 0;
      const paraH = para ? para.offsetHeight + parseFloat(getComputedStyle(para).marginTop || 0) : 0;
      full = Math.max(full, h);
      noChips = Math.max(noChips, h - chipsH);
      noPara = Math.max(noPara, h - chipsH - paraH);
    });
    let headH = parseFloat(getComputedStyle(stack).marginTop || 0);
    let sib = stack.previousElementSibling;
    while (sib) { headH += sib.offsetHeight + parseFloat(getComputedStyle(sib).marginTop || 0); sib = sib.previousElementSibling; }
    const railH = rail.offsetHeight + parseFloat(getComputedStyle(rail).marginTop || 0);
    stack.style.minHeight = prevStack;
    rail.style.display = prevRail;
    sec.__cinKey = key;
    sec.__cin = { full: full, noChips: noChips, noPara: noPara, headH: headH, railH: railH };
    return sec.__cin;
  }
  
  function fslProof(sec, p) {
    const h = window.innerHeight;
    const short = h < 860;
    const tight = h < 740;
    const set = (sel, prop, val) => { const el = sec.querySelector(sel); if (el) el.style[prop] = val; };
    set("[data-fsl-proof-grid]", "padding", tight ? "58px 24px 30px" : short ? "72px clamp(24px,5vw,64px) 38px" : "");
    set("[data-fsl-proof-title]", "fontSize", tight ? "clamp(26px,3.4vw,40px)" : short ? "clamp(28px,3.9vw,50px)" : "");
    set("[data-fsl-proof-title]", "marginTop", tight ? "10px" : "");
    set("[data-fsl-proof-stats]", "marginTop", tight ? "18px" : short ? "26px" : "");
    set("[data-fsl-proof-quotes]", "marginTop", tight ? "16px" : short ? "24px" : "");
    set("[data-fsl-proof-quotes]", "gap", tight ? "8px" : "");
    set("[data-fsl-proof-cta]", "marginTop", tight ? "18px" : short ? "24px" : "");
    sec.querySelectorAll("[data-fsl-proof-quote]").forEach((el, i) => { el.style.display = tight && i > 0 ? "none" : ""; });
    sec.querySelectorAll("[data-fsl-proof-cta] button").forEach((el) => { el.style.padding = tight ? "11px 22px" : ""; el.style.fontSize = tight ? "14px" : ""; });
    sec.querySelectorAll("[data-fsl-count]").forEach((el, i) => {
      const to = Number(el.getAttribute("data-to") || 0);
      const suffix = el.getAttribute("data-suffix") || "";
      const t = eo(sg(p, .08 + i * .06, .5 + i * .06));
      const v = Math.round(to * t);
      el.textContent = (to >= 1000000 ? (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M" : v.toLocaleString()) + suffix;
    });
    const clips = [].slice.call(sec.querySelectorAll("[data-fsl-clip]"));
    const n = Math.max(1, clips.length);
    const mid = (n - 1) / 2;
    const wrap = clips.length ? clips[0].parentElement : null;
    const cw = wrap ? wrap.clientWidth : 0;
    const gap = wrap ? parseFloat(getComputedStyle(wrap).columnGap || 14) || 14 : 14;
    const grid = wrap ? wrap.parentElement : null;
    let availH = 0;
    if (grid) {
      const gs = getComputedStyle(grid);
      availH = grid.clientHeight - parseFloat(gs.paddingTop || 0) - parseFloat(gs.paddingBottom || 0);
    }
    if (!(availH > 60)) availH = window.innerHeight * .7;
    const ar = 9 / 16;
    const wFan = Math.min(300, (cw - gap * (n - 1)) / n, availH * ar);
    const wWrap = Math.min(300, (cw - gap) / 2, ((availH - gap) / 2) * ar);
    const fan = wFan >= wWrap;
    const w = Math.max(74, fan ? wFan : wWrap);
    if (wrap) {
      wrap.style.flexWrap = fan ? "nowrap" : "wrap";
      wrap.style.justifyContent = "center";
      wrap.style.alignContent = "center";
      wrap.style.marginLeft = "auto";
      wrap.style.marginRight = "auto";
      const perRow = fan ? n : 2;
      wrap.style.maxWidth = Math.ceil(perRow * w + (perRow - 1) * gap + 1) + "px";
    }
    clips.forEach((el, i) => {
      el.style.flex = "0 0 auto";
      el.style.maxWidth = "none";
      el.style.width = Math.floor(w) + "px";
      const off = Math.abs(i - mid) / Math.max(.5, mid);
      const t = eo(sg(p, .06 + i * .06, .46 + i * .06));
      const lift = fan ? (1 - off) * 54 : 0;
      const rot = fan ? (i - mid) * 7 : (i % 2 === 0 ? -3 : 3);
      el.style.opacity = String(t);
      el.style.transform = "translate3d(0," + (-lift * t + (1 - t) * 60).toFixed(1) + "px,0) rotate(" + (rot * t).toFixed(2) + "deg)";
    });
  }
  /* ─────────────────────────── END PROTOTYPE CODE ─────────────────────────── */

  /* ------------------------------------------------------------------ wiring */

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * With reduced motion asked for, paint every animated element at its END
   * state once and stop. data-a elements finish at opacity 1 and no transform,
   * which is exactly the layout with the animation removed rather than a
   * half-finished frame.
   */
  function settle() {
    document.querySelectorAll('[data-a]').forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    document.querySelectorAll('[data-fsl-step]').forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    var scrim = document.querySelector('[data-fsl-hero-scrim]');
    if (scrim) scrim.style.opacity = '1';
    var hint = document.querySelector('[data-fsl-hint]');
    if (hint) hint.style.display = 'none';
  }

  function hasScenes() {
    return !!document.querySelector('[data-scene], [data-fs-stage]');
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      fsPaintStage();
      fslVideos();
    });
  }

  function start() {
    if (!hasScenes()) return;
    if (reduced) { settle(); return; }
    fsBindCompare();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    // A scene's height is in vh, so it changes when a mobile browser's address
    // bar hides. Repaint on that too, or the scrub drifts against the scroll.
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Shopify's theme editor rebuilds a section in place without reloading the
  // page. Without this, a merchant editing the homepage sees the motion die.
  document.addEventListener('shopify:section:load', function () {
    if (reduced) { settle(); return; }
    onScroll();
  });
})();
